import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import logger from "../../../utils/logger";
import { useCallback, useMemo } from "react";
import { collection, limit, onSnapshot, or, query, where, orderBy as firestoreOrderBy } from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { toast } from "react-toastify";
import { orderBy } from "lodash";
import moment from "moment-timezone";
import { v4 as uuid } from "uuid";
import { addRecordToTop } from "../../../utils/dbUtils";
import Notification from "../../../models/Notification";
import NewNotificationToast from "../../../components/Notifications/NewNotificationToast";

const initialState = {
	notifications: []
};

// one listener per client, held outside the store because the unsubscribe is not serialisable.
// the key is what makes re-attaching idempotent: login() runs again whenever a token claim
// changes, and without this every one of those left another live snapshot behind.
let notificationsUnsubscribe = null;
let notificationsListenerKey = null;

// the caller gates this listener on the profile read, but that read can be answered from the local
// cache while the users document it came from has not committed on the server yet - and the
// `exists(/users/{uid})` in the `isSignedIn()` rule only sees committed data. so a rejection is
// re-attached rather than trusted as final, a bounded number of times.
const LISTEN_RETRY_DELAYS_MS = [1000, 3000, 8000];

let notificationsRetryCount = 0;
let notificationsRetryKey = null;
let notificationsRetryTimeoutId = null;

const stopNotificationsListener = () => {
	if (isNotNullOrUndefined(notificationsUnsubscribe)) {
		notificationsUnsubscribe();
	}

	notificationsUnsubscribe = null;
	notificationsListenerKey = null;
};

const clearPendingNotificationsRetry = () => {
	if (isNotNullOrUndefined(notificationsRetryTimeoutId)) {
		clearTimeout(notificationsRetryTimeoutId);
	}

	notificationsRetryTimeoutId = null;
};

const cancelNotificationsRetry = () => {
	clearPendingNotificationsRetry();

	notificationsRetryCount = 0;
	notificationsRetryKey = null;
};

const notificationReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'UPDATE_NOTIFICATION':
			return {
				...state,
				notifications: addRecordToTop('id', payload, state.notifications)
			};
		case 'LOAD_NOTIFICATIONS':
			return {
				...state,
				notifications: payload
			};
		case 'RESET_NOTIFICATIONS':
			return {
				...state,
				...initialState
			};
		default:
			return state;
	}
};

export const loadNotifications = (list) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'LOAD_NOTIFICATIONS', payload: list });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateNotification = (notification) => {
	return async (dispatch) => {
		try {
			if (isObjectNotEmpty(notification)) {
				dispatch({ type: 'UPDATE_NOTIFICATION', payload: { ...notification } });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const markNotificationAsRead = async (notificationId) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const notificationState = { ...state.notificationState };
			const { notifications } = notificationState;

			let existingNotification = notifications.find((n) => n.id === notificationId);
			if (isObjectNotEmpty(existingNotification)) {
				let success = await Notification.markRead(notificationId);
				if (isBooleanTrue(success)) {
					dispatch({
						type: 'UPDATE_NOTIFICATION',
						payload: {
							...existingNotification,
							read: true,
							readAt: moment().utc().toDate()
						}
					});

					return true;
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return false;
	}
};

export const markNotificationAsUnread = async (notificationId) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const notificationState = { ...state.notificationState };
			const { notifications } = notificationState;

			let existingNotification = notifications.find((n) => n.id === notificationId);
			if (isObjectNotEmpty(existingNotification)) {
				let success = await Notification.markUnread(notificationId);
				if (isBooleanTrue(success)) {
					dispatch({
						type: 'UPDATE_NOTIFICATION',
						payload: {
							...existingNotification,
							read: false,
							readAt: null
						}
					});

					return true;
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return false;
	}
};

export const resetNotifications = () => {
	return async (dispatch) => {
		try {
			cancelNotificationsRetry();
			stopNotificationsListener();

			dispatch({ type: 'RESET_NOTIFICATIONS', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const refreshNotifications = (userId, isStaff = false) => {
	return async (dispatch) => {
		try {
			if (isStringNotEmpty(userId)) {
				// the query names the customer but runs on whoever is signed in, and the `notifications`
				// list rule is `isSignedIn()` with no ownership clause - so attaching for anyone but the
				// current user reads their inbox under this session's credentials. every route to the
				// listener passes through here: the profile wait, the retry timer below, and the hook.
				if (auth.currentUser?.uid !== userId) {
					return;
				}

				const listenerKey = `${userId}|${isBooleanTrue(isStaff)}`;
				if (listenerKey === notificationsListenerKey) {
					return;
				}

				clearPendingNotificationsRetry();
				stopNotificationsListener();

				// the budget belongs to one listener, so only a different user or role starts it over -
				// resetting it here unconditionally would make the retry re-entry an unbounded loop.
				if (listenerKey !== notificationsRetryKey) {
					notificationsRetryKey = listenerKey;
					notificationsRetryCount = 0;
				}

				logger.logDebugEvent('REFRESH_NOTIFICATIONS', 'Refreshed notifications', false);

				let isInitialSnapshot = true;

				const baseConstraints = isBooleanTrue(isStaff)
					? [
						or(
							where("userId", "==", userId),
							where("toStaff", "==", true)
						)
					]
					: [where("userId", "==", userId)];

				// Only listen to the 10 newest, and Firestore will push any new docs that become part of this window.
				const notificationQuery = query(
					collection(db, "notifications"),
					...baseConstraints,
					firestoreOrderBy("createdAt", "desc"),
					limit(10)
				);

				// Start listening to the query.
				notificationsUnsubscribe = onSnapshot(notificationQuery, function (snapshot) {
					const changes = snapshot.docChanges({ includeMetadataChanges: false });
					// console.log('notifications updated');

					// If this is the first snapshot, skip all changes
					if (isInitialSnapshot) {
						isInitialSnapshot = false;
						notificationsRetryCount = 0;

						let list = snapshot.docs.map((doc) => {
							return {
								id: doc.id, // append document id to each document
								...doc.data()
							};
						});
						// console.log('notifications', list);
						let orderedList = orderBy(list, ['createdAt'], ['desc']);
						// console.log('ordered notifications', orderedList);
						dispatch(loadNotifications(orderedList));

						return;
					}

					for (const change of changes) {
						// console.log('change', change);
						if (change.type === "added") {
							const newNotification = { id: change.doc.id, ...change.doc.data() };
							// console.log("New notification: ", newNotification);

							dispatch(updateNotification(newNotification));

							toast(<NewNotificationToast notification={newNotification} />, {
								toastId: newNotification?.id ?? `new-message-${uuid()}`,
							});
						}
					}
				}, (error) => {
					// a rejected listen is terminal - firestore never retries it - so the handle has to be
					// dropped or a dead listener stays recorded as attached and nothing re-attaches.
					stopNotificationsListener();

					const hasRetryRemaining = notificationsRetryCount < LISTEN_RETRY_DELAYS_MS.length;
					logger.logErrorEvent('REFRESH_NOTIFICATIONS_FAILED', error, `notifications listener: ${getErrorMessage(error)} (attempt ${notificationsRetryCount + 1}${hasRetryRemaining ? '' : ', giving up'})`, true);

					if (hasRetryRemaining) {
						const retryDelayMs = LISTEN_RETRY_DELAYS_MS[notificationsRetryCount];
						notificationsRetryCount = notificationsRetryCount + 1;

						notificationsRetryTimeoutId = setTimeout(() => {
							notificationsRetryTimeoutId = null;
							dispatch(refreshNotifications(userId, isStaff));
						}, retryDelayMs);
					}
				});

				notificationsListenerKey = listenerKey;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const useNotifications = () => {
	const dispatch = useDispatch();

	const notifications = useSelector((appState) => appState.notificationState.notifications);

	const getNotification = useCallback((notificationId) => {
		return notifications.find((notification) => notification.id === notificationId) ?? null;
	}, [notifications]);

	const sendNotification = async (userId, toStaff = false, subject, body, type = 'INFO', severity = 'LOW', entityType, entityId, entityStatus, eventType, ctaLabel, ctaUrl) => {
		const newNotification = await new Notification({
			userId: userId,
			toStaff: toStaff,

			subject: subject,
			body: body,
			type: type,
			severity: severity,

			entityType: entityType,
			entityId: entityId,
			entityStatus: entityStatus,

			eventType: eventType,

			cta: { label: ctaLabel, url: ctaUrl }
		}).create();
		if (isObjectNotEmpty(newNotification)) {
			return true;
		}

		return false;
	};

	const unreadNotificationCount = useMemo(() => {
		if (isListNotEmpty(notifications)) {
			return notifications.filter(i => isBooleanFalse(i.read) || isBooleanTrue(i.toStaff)).length;
		}

		return 0;
	}, [notifications]);

	return {
		NotificationState: useSelector((appState) => appState.notificationState),

		notifications: useSelector((appState) => appState.notificationState.notifications),
		unreadNotificationCount: unreadNotificationCount,

		sendNotification: sendNotification,
		getNotification: getNotification,
		refreshNotifications: (userId, isStaff) => dispatch(refreshNotifications(userId, isStaff)),

		loadNotifications: (list) => dispatch(loadNotifications(list)),
		updateNotification: (notification) => dispatch(updateNotification(notification)),
		markNotificationAsRead: (notificationId) => dispatch(markNotificationAsRead(notificationId)),
		markNotificationAsUnread: (notificationId) => dispatch(markNotificationAsUnread(notificationId)),
		resetNotifications: () => dispatch(resetNotifications())
	};
};

export default notificationReducer;